import { Permission } from '@act/shared/authorization';
import {
  MemberTransferReasonDto,
  MemberTransferRequestDto,
  MemberTransferRequestFormSchema,
  MemberTransferRequestFormType,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { Button, FlexContainer, FormBuilder, SelectOption } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Card,
  CardActions,
  CardContent,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { TrackingProvider } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import { membersAppSelectors } from 'redux/slices/memberSlice.slice';
import { memberTransferRequests } from 'redux/requests/memberTransfer.requests';
import { selectActingUser } from 'redux/slices/actingUserSlice.slice';
import {
  createMemberTransferRequestSuccess,
  getMemberTransferReasons,
  memberTransferRequestSelectors,
} from 'redux/slices/memberTransferRequestSlice.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { reemaColors } from 'toolkit/styles/colors';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { useToast } from '../app/ReusableToast';
import Loader from '../workflows/Loader';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { SCHEDULED_EVENT_DATE_FORMAT } from './scheduledEventForm.util';
import { createTransferRequestFormConfig } from './TransferRequestFormConfig';

export interface TransferRequestUniqueProps {
  requestMember: UserDto;
  transferRequest?: MemberTransferRequestDto;
}

export type TransferRequestDialogProps = GenericDialogProps<boolean> &
  TransferRequestUniqueProps;

export type TransferRequestDialogCallbackInput = unknown;

export const TransferRequestDialog = ({
  requestMember,
  transferRequest,
  open,
  setOpen,
}: TransferRequestDialogProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();

  const memberTransferReasons =
    memberTransferRequestSelectors.useSelectMemberTransferReasons();
  const memberTransferLoadingStatus =
    memberTransferRequestSelectors.useSelectMemberTransferSliceStatuses();
  const actingUser = useAppSelector(selectActingUser);

  const { control, getValues, setValue, watch, formState, handleSubmit } =
    useForm<MemberTransferRequestFormType>({
      defaultValues: {
        memberId: requestMember?.id,
        clientId: requestMember?.clientId,
        originatingGuideId: requestMember?.assignedGuideId,
        receivingGuideId: '',
        creatorId: actingUser?.id,
        transferReasonId: null,
        commentToReceivingGuide: '',
        stringDetail: '',
        numericDetail: null,
        timestampDetail: transferRequest?.timestampDetail
          ? DateTime.fromMillis(transferRequest.timestampDetail).toFormat(
              SCHEDULED_EVENT_DATE_FORMAT
            )
          : DateTime.now().toFormat(SCHEDULED_EVENT_DATE_FORMAT),
      },
      resolver: zodResolver(MemberTransferRequestFormSchema),
    });

  const watchTransferReasonId = parseInt(watch('transferReasonId')?.toString());
  const watchReceivingGuideId = watch('receivingGuideId');
  const [selectedTransferReason, setSelectedTransferReason] =
    useState<MemberTransferReasonDto>(null);

  const canAuditTransferRequest = membersAppSelectors.useHasPermissionOnMember(
    requestMember.id,
    Permission.CAN_AUDIT_MEMBER_TRANSFER_REQUESTS
  );

  const canAuditTransferRequestWithoutGuide =
    membersAppSelectors.useHasPermissionOnMember(
      requestMember.id,
      Permission.CAN_AUDIT_MEMBER_TRANSFER_REQUESTS_WITHOUT_GUIDE
    );

  // For members without an assigned guide, check the _WITHOUT_GUIDE permission
  const canAudit =
    canAuditTransferRequest ||
    (!requestMember?.assignedGuideId && canAuditTransferRequestWithoutGuide);

  const [
    createMemberTransferRequestResult,
    _createMemberTransferRequestLoadingStatus,
    createMemberTransferRequest,
  ] = useApiCall(memberTransferRequests.createMemberTransferRequest);
  useEffect(() => {
    const newTransferReason = memberTransferReasons.find(
      r => r.id === watchTransferReasonId
    );
    if (newTransferReason === selectedTransferReason) return;

    setSelectedTransferReason(newTransferReason);
  }, [
    memberTransferReasons,
    selectedTransferReason,
    setValue,
    transferRequest,
    watchReceivingGuideId,
    watchTransferReasonId,
  ]);

  const [addressOptions, setAddressOptions] =
    useState<SelectOption<number>[]>();

  useEffect(() => {
    if (requestMember) {
      const activeAddresses = requestMember?.addresses?.filter(a => !a.deletedAt) || [];
      const addressOptions: SelectOption<number>[] =
        activeAddresses.map(a => {
          const label = `${a.line1}, ${a.line2 ? a.line2 + ', ' : ''}${
            a.city
          }, ${a.state} ${a.zip}`;
          return { label, value: a.id };
        });
      setAddressOptions(addressOptions);
    }
  }, [requestMember, setValue]);

  useEffect(() => {
    async function dispatchGetMemberTransferReasons() {
      await dispatch(await getMemberTransferReasons({ params: null }));
    }

    if (memberTransferLoadingStatus.reasonsStatus === 'initial') {
      void dispatchGetMemberTransferReasons();
    }
  }, [dispatch, memberTransferLoadingStatus.reasonsStatus]);

  useEffect(() => {
    if (createMemberTransferRequestResult) {
      dispatch(
        createMemberTransferRequestSuccess(createMemberTransferRequestResult)
      );
      toast('Transfer request sent successfully');
      setOpen(false);
    }
  }, [createMemberTransferRequestResult, dispatch]);

  const { isDirty, isValid } = formState;

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  async function onSubmit(data: MemberTransferRequestFormType) {
    const values = getValues();

    const transferDate = values?.timestampDetail
      ? DateTime.fromFormat(values?.timestampDetail, SCHEDULED_EVENT_DATE_FORMAT)
          .startOf('day')
          .toMillis()
      : DateTime.local().startOf('day').toMillis();

    createMemberTransferRequest({
      body: {
        memberId: data.memberId,
        receivingGuideId: data.receivingGuideId,
        transferReasonId: data.transferReasonId,
        stringDetail: data.stringDetail,
        numericDetail: data.numericDetail,
        commentToReceivingGuide: data.commentToReceivingGuide,
        originatingGuideId: requestMember?.assignedGuideId ?? null,
        timestampDetail: transferDate,
        ...(canAudit && {
          approval: 'approve',
          effectiveAt:
            transferDate || DateTime.local().startOf('day').toMillis(),
        }),
      },
    });
  }

  const formConfig = createTransferRequestFormConfig({
    memberTransferReasons,
    selectedTransferReason,
    addressOptions,
    control,
    errors: formState.errors,
  });

  return (
    <TrackingProvider contextName="transfer-request-dialog">
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        hideBackdrop
        style={{ marginTop: '51px' }}
      >
        <DialogTitle
          style={{
            padding: '18px 36px',
            boxShadow:
              '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 1px 3px 1px rgba(0, 0, 0, 0.08)',
            zIndex: 2,
          }}
        >
          <FlexContainer direction="row" alignItems="center" gap={5.5}>
            <IconButton onClick={handleClose}>
              <ArrowBackIcon />
            </IconButton>
            Member Transfer Request
          </FlexContainer>
        </DialogTitle>
        <DialogContent
          style={{
            display: 'grid',
            justifyContent: 'center',
            paddingTop: '12px',
            backgroundColor: reemaColors.lightBackground,
            zIndex: 1,
          }}
        >
          {memberTransferLoadingStatus.reasonsStatus !== 'success' && (
            <Loader />
          )}
          {memberTransferLoadingStatus.reasonsStatus === 'success' && (
            <Card style={{ height: 'min-content', maxWidth: '570px' }}>
              <CardContent style={{ padding: '24px 24px 0' }}>
                <FlexContainer direction="column" margin="0 0 8px 0">
                  <Typography variant="titleLarge">
                    We need a little info about your request.
                  </Typography>
                </FlexContainer>
                <FlexContainer direction="column" maxWidth="525px">
                  <Typography variant="bodyLarge">
                    Providing these details will help us resolve the request. It
                    may take a few days for a change to be made.
                  </Typography>
                </FlexContainer>
                <FlexContainer direction="column" gap={2} margin="40px 0">
                  <FormBuilder
                    config={formConfig}
                    control={control}
                    formState={formState}
                  />
                </FlexContainer>
              </CardContent>
              <Divider style={{ marginTop: '48px' }} />
              <CardActions style={{ padding: '8px 24px' }}>
                <FlexContainer
                  direction="row"
                  justifyContent="flex-end"
                  width="100%"
                  gap={1}
                >
                  <Button onClick={handleClose} trackingName="cancel-button">
                    Cancel
                  </Button>
                  <Button
                    disabled={!isValid}
                    debounced
                    onClick={handleSubmit(onSubmit)}
                    type={'submit'}
                    variant="contained"
                    trackingName="send-transfer-request-button"
                  >
                    Send request
                  </Button>
                </FlexContainer>
              </CardActions>
            </Card>
          )}
        </DialogContent>
      </Dialog>
    </TrackingProvider>
  );
};
